import React, { useEffect, useState } from 'react';
import { Shell } from '../components/Shell';
import {
  EnterpriseSummary,
  AuthError,
  listEnterprises,
  createEnterprise,
  updateEnterprise,
  deleteEnterprise,
} from '../lib/auth';

/**
 * Super-admin dashboard: every enterprise on the deployment, with controls to
 * create, suspend / re-activate, and soft-delete. Only super-admins reach
 * this page (RequireSuperAdmin guard in App.tsx).
 */
export function SuperAdminEnterprises() {
  const [rows, setRows] = useState<EnterpriseSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [oneTimePw, setOneTimePw] = useState<{ email: string; password: string } | null>(null);

  const flash = (kind: 'ok' | 'err', text: string): void => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3500);
  };

  const load = async (): Promise<void> => {
    try {
      setRows(await listEnterprises());
    } catch (e) {
      setError(e instanceof AuthError ? e.message : 'Failed to load enterprises.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async (): Promise<void> => {
    if (!newName.trim() || !newAdminEmail.trim()) return;
    setCreating(true);
    try {
      const { admin } = await createEnterprise({
        name: newName.trim(),
        adminEmail: newAdminEmail.trim(),
        adminName: newAdminName.trim() || undefined,
      });
      flash('ok', `Enterprise created. Admin: ${admin.email}`);
      if (admin.oneTimePassword) {
        setOneTimePw({ email: admin.email, password: admin.oneTimePassword });
      }
      setNewName('');
      setNewAdminEmail('');
      setNewAdminName('');
      await load();
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (row: EnterpriseSummary): Promise<void> => {
    const next = !row.isActive;
    try {
      await updateEnterprise(row.id, { isActive: next });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
      flash('ok', next ? 'Workspace activated.' : 'Workspace suspended.');
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Update failed.');
    }
  };

  const removeOne = async (row: EnterpriseSummary): Promise<void> => {
    if (!window.confirm(`Soft-delete ${row.name}? Members will lose access.`)) return;
    try {
      await deleteEnterprise(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      flash('ok', 'Workspace deleted.');
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Delete failed.');
    }
  };

  return (
    <Shell active="enterprises">
      <div className="oa-root">
      <main className="oa-main">
        <h1 className="oa-title">Enterprises</h1>
        <p className="oa-sub">Platform-wide view. Every workspace, including suspended ones.</p>

        {error && <div className="oa-msg oa-msg--err" style={{ margin: '14px 0' }}>{error}</div>}

        <div className="oa-card" style={{ marginTop: 18, padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>Create new workspace</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <input
              className="oa-input"
              placeholder="Workspace name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ flex: '1 1 220px', minWidth: 200 }}
            />
            <input
              className="oa-input"
              type="email"
              placeholder="Admin email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              style={{ flex: '1 1 220px', minWidth: 200 }}
            />
            <input
              className="oa-input"
              placeholder="Admin name (optional)"
              value={newAdminName}
              onChange={(e) => setNewAdminName(e.target.value)}
              style={{ flex: '1 1 180px', minWidth: 160 }}
            />
            <button className="oa-btn oa-btn--primary" disabled={creating} onClick={() => void create()}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
          {oneTimePw && (
            <div className="oa-msg oa-msg--ok" style={{ marginTop: 12 }}>
              <div>One-time password for <strong>{oneTimePw.email}</strong> (shown once):</div>
              <code style={{ fontSize: 13 }}>{oneTimePw.password}</code>
              <div style={{ marginTop: 6 }}>
                <button className="oa-btn oa-btn--sm" onClick={() => void navigator.clipboard?.writeText(oneTimePw.password)}>
                  Copy
                </button>
                <button className="oa-btn oa-btn--sm" style={{ marginLeft: 8 }} onClick={() => setOneTimePw(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="oa-card" style={{ marginTop: 22, overflow: 'hidden' }}>
          <div className="oa-table-head">
            {['Workspace', 'Slug', 'Members', 'Active', 'Created', ''].map((h, i) => (
              <span key={i} className="oa-th">{h}</span>
            ))}
          </div>
          {rows.map((r) => (
            <div key={r.id} className="oa-row">
              <span className="oa-name">{r.name}</span>
              <span className="oa-cell-muted">{r.slug}</span>
              <span className="oa-cell-muted">
                {r.activeMemberCount} active · {r.memberCount} total
              </span>
              <span>
                <button
                  className={`oa-toggle${r.isActive ? ' oa-toggle--on' : ''}`}
                  title={r.isActive ? 'Suspend' : 'Activate'}
                  onClick={() => void toggleActive(r)}
                >
                  <span className="oa-toggle-thumb" />
                </button>
              </span>
              <span className="oa-cell-muted">{new Date(r.createdAt).toLocaleDateString()}</span>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="oa-btn oa-btn--sm oa-btn--danger" onClick={() => void removeOne(r)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--oa-muted)' }}>No enterprises yet.</div>
          )}
        </div>
      </main>

      {toast && <div className={`oa-toast oa-toast--${toast.kind}`}>{toast.text}</div>}
      </div>
    </Shell>
  );
}
